import { AnyAction } from 'redux';
import { combineEpics } from 'redux-observable';
import { from, Observable, of } from 'rxjs';
import { catchError, filter, switchMap, withLatestFrom } from 'rxjs/operators';
import { isActionOf } from 'typesafe-actions';

import { ApiError } from '@bolteu/react-api-gateway/es';

import { selectSelectedProvider } from 'rdx/account';
import { GlobalAppActions } from 'rdx/actions';
import { AppServices } from 'rdx/middleware';
import { GlobalAppState } from 'rdx/state';

import { getScheduleAsyncAction, updateScheduleAsyncAction } from './actions';

const getScheduleEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(getScheduleAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([_action, state]) => {
      const selectedProvider = selectSelectedProvider(state);

      if (!selectedProvider || !selectedProvider.providerId) {
        return of(getScheduleAsyncAction.failure(new Error('Missed selected provider id')));
      }

      return from(
        apiClient.portal.getProviderSchedule({
          provider_id: selectedProvider.providerId,
        }),
      ).pipe(
        switchMap((res) => {
          const sideEffectActions: AnyAction[] = [];

          return of(getScheduleAsyncAction.success(res), ...sideEffectActions);
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(getScheduleAsyncAction.failure(e));
        }),
      );
    }),
  );
};

const updateScheduleEpic = (
  action$: Observable<GlobalAppActions>,
  state$: Observable<GlobalAppState>,
  { apiClient, bugsnag }: AppServices,
): Observable<GlobalAppActions> => {
  return action$.pipe(
    filter(isActionOf(updateScheduleAsyncAction.request)),
    withLatestFrom(state$),
    switchMap(([action, _state]) => {
      const { providerId, schedule } = action.payload;

      if (!providerId) {
        return of(
          updateScheduleAsyncAction.failure(new Error('Missed items or selected provider')),
        );
      }

      return from(
        apiClient.portal.updateProviderSchedule({ provider_id: providerId, schedule }),
      ).pipe(
        switchMap(() => {
          return of(updateScheduleAsyncAction.success(), getScheduleAsyncAction.request());
        }),
        catchError((e) => {
          if (!ApiError.isApiError(e)) {
            bugsnag.notify(e);
          }
          return of(updateScheduleAsyncAction.failure(e));
        }),
      );
    }),
  );
};

export const scheduleEpic = combineEpics(getScheduleEpic, updateScheduleEpic);
